import React from "react";
import {
  Tag,
  Heading,
  Lead,
  PageHero,
  Section,
  BtnPrimary,
} from "../components/UI";
import StatsBar from "../components/StatsBar";
import Reveal from "../components/Reveal";
import styles from "./About.module.css";

const TRAINING_PORTFOLIO = [
  { icon: "☕", color: "rgba(224,102,50,0.18)", t: "Java Full Stack", d: "End-to-end development covering front-end, back-end, and databases." },
  { icon: "🔷", color: "rgba(81,43,212,0.18)", t: ".NET", d: "Build scalable enterprise applications with Microsoft's development framework." },
  { icon: "🐍", color: "rgba(16,185,129,0.18)", t: "Python", d: "Versatile programming for web, automation, and data science." },
  { icon: "🤖", color: "rgba(139,92,246,0.18)", t: "AI & ML", d: "Harness Artificial Intelligence and Machine Learning for innovation." },
  { icon: "☁️", color: "rgba(0,161,224,0.18)", t: "Salesforce", d: "CRM expertise in Salesforce administration and development." },
  { icon: "🧪", color: "rgba(245,158,11,0.18)", t: "QA Automation", d: "Modern automation testing tools to ensure software quality." },
  { icon: "📊", color: "rgba(240,194,53,0.18)", t: "Power BI", d: "Transform data into insights with Microsoft's BI platform." },
  { icon: "🔄", color: "rgba(26,60,255,0.18)", t: "DevOps", d: "Streamline development and operations with CI/CD, automation, and cloud practices." },
  { icon: "🗄️", color: "rgba(16,185,129,0.18)", t: "Data Engineering", d: "Design and manage scalable data pipelines and architectures." },
  { icon: "📋", color: "rgba(255,77,46,0.18)", t: "Business Analyst", d: "Develop analytical and communication skills to bridge business and technology." },
];

const VALUES = [
  {
    icon: "🎯",
    t: "Customized Learning Paths",
    d: "Tailored programs for individuals and corporate teams.",
  },
  {
    icon: "🛠️",
    t: "Hands-On Experience",
    d: "Real-world projects and case studies.",
  },
  {
    icon: "📈",
    t: "Industry-Relevant Curriculum",
    d: "Updated to match evolving market trends.",
  },
  {
    icon: "🎓",
    t: "Career Growth Support",
    d: "Certifications, job readiness, and professional advancement.",
  },
];

export default function About() {
  return (
    <>
      <PageHero
        tag="About Us"
        title="Transforming Careers and Organizations Through Training"
        subtitle="Metawave Solutions delivers individual, group, and corporate training programs that empower learners with practical, industry-relevant skills to excel in today's dynamic technology landscape."
      />

      {/* ── Mission ───────────────────────────────────────────── */}
      <Section variant="paper">
        <div className={styles.storyGrid} style={{ gridTemplateColumns: "1fr", maxWidth: "760px", margin: "0 auto" }}>
          <div>
            <Tag>Our Mission</Tag>
            <Heading>Who We Are</Heading>
            <p className={styles.bodyText}>
              At Metawave Solutions, we are dedicated to transforming careers
              and organizations through individual and group training
              programs as well as corporate training solutions. Our mission
              is to empower learners with practical, industry-relevant skills
              that prepare them to excel in today's dynamic technology
              landscape.
            </p>
            <p className={styles.bodyText}>
              We combine expert-led instruction, hands-on projects, and
              career-focused learning paths to ensure that every participant
              gains the confidence and expertise to succeed.
            </p>
            <BtnPrimary to="/contact-us" style={{ marginTop: "1.8rem" }}>
              Work With Us
            </BtnPrimary>
          </div>
        </div>
      </Section>

      <StatsBar />

      {/* ── Training Portfolio ───────────────────────────────── */}
      <Section variant="ink">
        <Tag light>What We Teach</Tag>
        <Heading light>Our Training Portfolio</Heading>
        <Lead light>
          We specialize in a wide range of technologies and professional
          skills.
        </Lead>
        <div className={styles.portfolioGrid}>
          {TRAINING_PORTFOLIO.map((p, i) => (
            <Reveal key={i} delay={(i % 2) * 0.1} className={styles.portfolioCard}>
              <div className={styles.portfolioIcon} style={{ background: p.color }}>{p.icon}</div>
              <h4 className={styles.portfolioTitle}>{p.t}</h4>
              <p className={styles.portfolioDesc}>{p.d}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ── Values ────────────────────────────────────────────── */}
      <Section variant="white">
        <Tag>Why Choose Us</Tag>
        <Heading>Why Choose Metawave Solutions?</Heading>
        <div className={styles.valuesGrid}>
          {VALUES.map((v, i) => (
            <Reveal key={i} delay={(i % 2) * 0.1} className={styles.valueCard}>
              <div className={styles.valueIcon}>{v.icon}</div>
              <h3 className={styles.valueTitle}>{v.t}</h3>
              <p className={styles.valueDesc}>{v.d}</p>
            </Reveal>
          ))}
        </div>
        <Lead style={{ marginTop: "2.5rem" }}>
          At Metawave Solutions, we don't just deliver training — we create
          opportunities for growth, innovation, and success.
        </Lead>
      </Section>
    </>
  );
}
